"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Icon from "@/components/ui/AppIcon";
import type { PageTexts } from "@/lib/siteTexts";

const cliLines = [
  { prefix: "$", cmd: "oblako vm create --name prod-server --cpu 8 --ram 16 --disk 200", delay: 0 },
  { prefix: ">", cmd: "Creating virtual machine...", delay: 800, color: "text-[#8BA3CC]" },
  { prefix: ">", cmd: "✓ VM prod-server deployed in 12s", delay: 1600, color: "text-[#00FF88]" },
  { prefix: "$", cmd: "oblako storage attach --vm prod-server --size 500GB", delay: 2400 },
  { prefix: ">", cmd: "✓ Block storage attached. IP: 185.123.45.67", delay: 3200, color: "text-[#00FF88]" },
];

const floatingStats = [
  { n: 1, icon: "ShieldCheckIcon", color: "#00D4FF" },
  { n: 2, icon: "MapPinIcon", color: "#4F7CFF" },
  { n: 3, icon: "BoltIcon", color: "#7C3AED" },
] as const;

export default function HeroSection({ texts: t }: { texts: PageTexts<"homepage"> }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [visibleLines, setVisibleLines] = useState<number[]>([]);
  const [typedCmd, setTypedCmd] = useState("");
  const [currentLineIndex, setCurrentLineIndex] = useState(0);

  // Particle canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles: { x: number; y: number; vx: number; vy: number; size: number; opacity: number }[] = [];
    for (let i = 0; i < 80; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        size: Math.random() * 1.5 + 0.5,
        opacity: Math.random() * 0.4 + 0.1,
      });
    }

    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(79, 124, 255, ${p.opacity})`;
        ctx.fill();
      });

      // Draw connections
      particles.forEach((p1, i) => {
        particles.slice(i + 1).forEach((p2) => {
          const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(79, 124, 255, ${0.08 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });

      animId = requestAnimationFrame(animate);
    };
    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // CLI typewriter
  useEffect(() => {
    if (currentLineIndex >= cliLines.length) return;
    const line = cliLines[currentLineIndex];
    const timeout = setTimeout(() => {
      setVisibleLines((prev) => [...prev, currentLineIndex]);
      let charIdx = 0;
      const typeInterval = setInterval(() => {
        charIdx++;
        setTypedCmd(line.cmd.slice(0, charIdx));
        if (charIdx >= line.cmd.length) {
          clearInterval(typeInterval);
          setTimeout(() => {
            setCurrentLineIndex((prev) => prev + 1);
            setTypedCmd("");
          }, 300);
        }
      }, 30);
    }, line.delay);
    return () => clearTimeout(timeout);
  }, [currentLineIndex]);

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#050B18]">
      {/* Animated grid */}
      <div className="absolute inset-0 grid-bg opacity-60" />

      {/* Particle canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" />

      {/* Radial glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-radial-blue opacity-40 pointer-events-none" />
      <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] rounded-full bg-[rgba(0,212,255,0.06)] blur-[100px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass neon-border">
              <span className="status-dot status-operational" />
              <span className="text-xs font-semibold tracking-wider text-[#00D4FF] uppercase">
                {t["hero.badge"]}
              </span>
            </div>

            {/* Headline */}
            <div>
              <h1 className="font-display font-bold text-5xl lg:text-6xl xl:text-7xl leading-[1.05] text-white">
                {t["hero.title1"]}
                <br />
                <span className="gradient-text">{t["hero.title2"]}</span>
                <br />
                {t["hero.title3"]}
              </h1>
            </div>

            <p className="text-[#8BA3CC] text-lg leading-relaxed max-w-lg">
              {t["hero.subtitle"]}
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4">
              <Link href={t["hero.primary.href"]} className="btn-primary text-sm font-bold px-7 py-3.5 rounded-lg flex items-center gap-2">
                <Icon name="ServerIcon" size={18} />
                {t["hero.primary.label"]}
              </Link>
              <Link href={t["hero.secondary.href"]} className="btn-secondary text-sm font-semibold px-7 py-3.5 rounded-lg flex items-center gap-2">
                {t["hero.secondary.label"]}
                <Icon name="ArrowRightIcon" size={16} />
              </Link>
            </div>

            {/* Mini stats */}
            <div className="flex flex-wrap gap-6 pt-2">
              {floatingStats.map((stat) => (
                <div key={stat.n} className="flex items-center gap-2">
                  <div
                    className="w-7 h-7 rounded-md flex items-center justify-center"
                    style={{ background: `${stat.color}18` }}
                  >
                    <Icon name={stat.icon} size={14} className="" style={{ color: stat.color }} />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white">{t[`hero.stat${stat.n}.value`]}</div>
                    <div className="text-xs text-[#4A6080]">{t[`hero.stat${stat.n}.label`]}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: CLI Terminal */}
          <div className="animate-float">
            <div className="terminal p-0 overflow-hidden rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
              {/* Terminal header */}
              <div className="flex items-center gap-2 px-5 py-4 border-b border-[rgba(0,212,255,0.1)] bg-[rgba(0,0,0,0.3)]">
                <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                <div className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
                <div className="w-3 h-3 rounded-full bg-[#28C840]" />
                <span className="ml-3 text-xs text-[#4A6080] font-mono">oblako-cli — bash</span>
              </div>

              {/* Terminal body */}
              <div className="p-6 space-y-2 min-h-[280px]">
                {cliLines.map((line, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    {idx < currentLineIndex ? (
                      <>
                        <span className={`text-xs font-mono ${idx % 2 === 0 ? "text-[#00D4FF]" : "text-[#8BA3CC]"}`}>
                          {line.prefix}
                        </span>
                        <span className={`text-xs font-mono ${line.color || "text-[#E8F0FF]"}`}>
                          {line.cmd}
                        </span>
                      </>
                    ) : idx === currentLineIndex ? (
                      <>
                        <span className="text-xs font-mono text-[#00D4FF]">{line.prefix}</span>
                        <span className="text-xs font-mono text-[#E8F0FF]">
                          {typedCmd}
                          <span className="animate-pulse">▋</span>
                        </span>
                      </>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>

            {/* Floating metric cards */}
            <div className="mt-4 grid grid-cols-3 gap-3">
              {([1, 2, 3] as const).map((n) => ({
                value: t[`hero.metric${n}.value`],
                label: t[`hero.metric${n}.label`],
                trend: t[`hero.metric${n}.trend`],
              })).map((m, i) => (
                <div key={i} className="glass rounded-xl p-4 neon-border-blue text-center card-hover">
                  <div className="text-lg font-bold gradient-text">{m.value}</div>
                  <div className="text-[10px] text-[#4A6080] mt-0.5">{m.label}</div>
                  <div className="text-[10px] text-[#00FF88] mt-1">{m.trend}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#4A6080]">
        <span className="text-xs tracking-widest uppercase">{t["hero.scroll"]}</span>
        <div className="w-5 h-8 rounded-full border border-[rgba(79,124,255,0.3)] flex justify-center pt-2">
          <div className="w-1 h-2 bg-[#4F7CFF] rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
}